import { combineReducers } from 'redux';
import { IState } from '@redux/reducer';
import { Types } from './api';

import {
  ILoadStaffList,
  ILoadStaffListSuccess,
  ILoadStaffListFailure,
  ILoadPatientDetails,
  ILoadPatientDetailsSuccess,
  ILoadPatientDetailsFailure,
  ICreatePatient,
  ICreatePatientSuccess,
  ICreatePatientFailure,
} from './actions';

type StaffList = Types.IKemtaiStaffMember[];
type StaffRequest = {
  status: 'idle' | 'loading' | 'success' | 'failure';
  isLoading: boolean;
  errorStatus?: number;
};

const initialStaffRequest: StaffRequest = {
  status: 'idle',
  isLoading: false,
};

const initialStatusState = {
  isLoading: false,
  isSubmitting: false,
};

const status = (
  state = initialStatusState,
  action:
    | ILoadStaffList
    | ILoadStaffListSuccess
    | ILoadStaffListFailure
    | ILoadPatientDetails
    | ILoadPatientDetailsSuccess
    | ILoadPatientDetailsFailure
    | ICreatePatient
    | ICreatePatientSuccess
    | ICreatePatientFailure,
) => {
  switch (action.type) {
    case 'kemtai/load-staff-list':
      if (action.payload.directChildOrganisationSlug) return state;
      return { ...state, isLoading: true };
    case 'kemtai/load-patient-details':
      return { ...state, isLoading: true };
    case 'kemtai/load-staff-list-success':
    case 'kemtai/load-staff-list-failure':
      if (action.payload.directChildOrganisationSlug) return state;
      return { ...state, isLoading: false };
    case 'kemtai/load-patient-details-success':
    case 'kemtai/load-patient-details-failure':
      return { ...state, isLoading: false };
    case 'kemtai/create-patient':
      return { ...state, isSubmitting: true };
    case 'kemtai/create-patient-success':
    case 'kemtai/create-patient-failure':
      return { ...state, isSubmitting: false };
    default:
      return state;
  }
};

const initialStaffList: StaffList = [];

const staffList = (state = initialStaffList, action: ILoadStaffListSuccess) => {
  switch (action.type) {
    case 'kemtai/load-staff-list-success':
      return action.payload.directChildOrganisationSlug ? state : action.payload.staffList;
    default:
      return state;
  }
};

const directChildOrganisationStaffRequests = (
  state: Record<string, StaffRequest> = {},
  action: ILoadStaffList | ILoadStaffListSuccess | ILoadStaffListFailure,
): Record<string, StaffRequest> => {
  const directChildOrganisationSlug = action.payload?.directChildOrganisationSlug;
  if (!directChildOrganisationSlug) return state;
  const current = state[directChildOrganisationSlug] || initialStaffRequest;

  switch (action.type) {
    case 'kemtai/load-staff-list':
      return {
        ...state,
        [directChildOrganisationSlug]: {
          ...current,
          status: 'loading',
          isLoading: true,
          errorStatus: undefined,
        },
      };
    case 'kemtai/load-staff-list-success':
      return {
        ...state,
        [directChildOrganisationSlug]: {
          status: 'success',
          isLoading: false,
        },
      };
    case 'kemtai/load-staff-list-failure':
      return {
        ...state,
        [directChildOrganisationSlug]: {
          status: 'failure',
          isLoading: false,
          errorStatus: action.payload.status,
        },
      };
    default:
      return state;
  }
};

const directChildOrganisationStaffLists = (
  state: Record<string, StaffList> = {},
  action: ILoadStaffListSuccess,
) => {
  if (
    action.type !== 'kemtai/load-staff-list-success' ||
    !action.payload.directChildOrganisationSlug
  ) {
    return state;
  }

  return {
    ...state,
    [action.payload.directChildOrganisationSlug]: action.payload.staffList,
  };
};

const initialPatientDetails: any = {};

const patientDetails = (
  state = initialPatientDetails,
  action: ILoadPatientDetailsSuccess | ICreatePatientSuccess,
) => {
  switch (action.type) {
    case 'kemtai/load-patient-details-success':
    case 'kemtai/create-patient-success':
      const s = { ...state };
      s[action.payload.patient.app_user_id] = action.payload.patient;
      return s;
    default:
      return state;
  }
};

export const selectIsLoading = (state: IState) => {
  return state.kemtai.status.isLoading;
};

export const selectIsSubmitting = (state: IState) => {
  return state.kemtai.status.isSubmitting;
};

export const selectStaffList = (state: IState): [boolean, StaffList] => {
  return [state.kemtai.status.isLoading, state.kemtai.staffList];
};

export const selectDirectChildOrganisationStaffList = (directChildOrganisationSlug: string) => (
  state: IState,
): [StaffRequest, StaffList] => [
  state.kemtai.directChildOrganisationStaffRequests[directChildOrganisationSlug] ||
    initialStaffRequest,
  state.kemtai.directChildOrganisationStaffLists[directChildOrganisationSlug] || initialStaffList,
];

export const selectPatientDetailsForAppUserId = (appUserId: string) => (state: IState): any => {
  if (!appUserId) return undefined;
  return state.kemtai.patientDetails[appUserId];
};

export default combineReducers({
  status,
  staffList,
  directChildOrganisationStaffRequests,
  directChildOrganisationStaffLists,
  patientDetails,
});
